import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Payload describing a single failed-backend-response notice. `header` and
 * `message` are pre-translated, user-facing copy (the interceptor resolves
 * them through vue-i18n before calling `show`); `detail` is the raw backend
 * error message / code and passes through untranslated.
 */
export interface ErrorDialogPayload {
  header: string;
  message: string;
  detail?: string;
}

/**
 * Global error-dialog store. A single active dialog at a time — a newer error
 * replaces the current one (no queue), which keeps the surface calm: the user
 * never has to dismiss a stack of stale notices. The dialog is purely
 * presentational; dismissing it only clears `current` and never navigates,
 * reloads, or unmounts the view underneath.
 *
 * Setup-store style to match `src/stores/org.ts`.
 */
export const useErrorDialogStore = defineStore('errorDialog', () => {
  const current = ref<ErrorDialogPayload | null>(null);

  const isOpen = computed(() => current.value !== null);

  /**
   * Surface an error notice. Replaces any currently-shown notice.
   * @param payload The header / message / optional detail to display.
   */
  function show(payload: ErrorDialogPayload): void {
    current.value = payload;
  }

  /**
   * Hide the current notice. Idempotent — safe to call when nothing is shown.
   */
  function dismiss(): void {
    current.value = null;
  }

  return { current, isOpen, show, dismiss };
});
